import React, {FC, useState} from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {downloadBlob} from '../../../../libs/shared/core/src/helpers/utilities/downloadHelpers';
import {InterfaceEventsEnum} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {OkCancelDialog} from '../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {SeparatorsEnum} from '../../helpers/analyticsExportHelpers';
import {useAnalyticsTracking} from '../../helpers/tracking/useAnalyticsTracking';
import {MetricExportTypesEnum, separatorConfigs} from '../exports/helpers/common';
import {ExportInfoText} from '../exports/helpers/exportInfoText';
import {ExportSeparatorInput} from '../exports/helpers/exportSeparatorInput';
import {ExportType, ExportTypeInput} from '../exports/helpers/exportTypeInput';
import {tableDataToCsv} from '../exports/helpers/metricExports';
import {CustomTableColumn, TableData} from '../reportElements/table/tableHelpers';

/*
 * Props.
 */

interface TableExportViewDialogContainerProps {
  onRequestClose: () => void;
  data: Array<TableData>;
  columns: Array<CustomTableColumn<TableData>>;
  title: string;
}

/*
 * Constants.
 */

const exportType: ExportType = {
  value: MetricExportTypesEnum.TABLE_DATA,
  title: <Trans id="analytics-exports-table-data-export-title">Table data</Trans>,
};

/*
 * Component.
 */

export const TableExportViewDialog: FC<TableExportViewDialogContainerProps> = ({
  onRequestClose,
  data,
  columns,
  title,
}) => {
  const [selectedSeparator, setSelectedSeparator] = useState(separatorConfigs[0]);

  const trackAnalytics = useAnalyticsTracking();

  const onRequestExportClick = (separator: SeparatorsEnum) => {
    const csv = tableDataToCsv(separator, columns, data);
    const blob = new Blob([csv], {type: 'text/csv'});

    const timeStamp = Date.now().toString();
    const fileName = `export-${title}-${timeStamp}.csv`;

    downloadBlob(fileName, blob);

    trackAnalytics(InterfaceEventsEnum.ANALYTICS_VIEW_EXPORT_TABLE, {
      export_type: MetricExportTypesEnum.TABLE_DATA,
      table: title,
    });

    // Close the dialog after a request export click - there is not a way to verify
    // that the download was actually successful (since it would require accessing the
    // users local filesystem).
    onRequestClose();
  };

  return (
    <OkCancelDialog
      title={<Trans id="table-export-dialog-title">Export table data</Trans>}
      okButtonLabel={<Trans id="export-request-button">Download data</Trans>}
      okButtonVariant={ButtonVariantsEnum.PRIMARY}
      onRequestClose={onRequestClose}
      onOkClick={() => onRequestExportClick(selectedSeparator.value)}
      width={500}
    >
      <ExportTypeInput availableExportTypes={[exportType]} selectedExportType={exportType} />
      <ExportSeparatorInput
        separatorConfigs={separatorConfigs}
        selectedSeparator={selectedSeparator}
        setSelectedSeparator={setSelectedSeparator}
      />
      <ExportInfoText
        text={
          <Trans id="table-export-info-text">
            Exports are CSV files with the raw data used in the analytics dashboard.
          </Trans>
        }
        subtext={<Trans id="table-export-info-subtext">View documentation</Trans>}
      />
    </OkCancelDialog>
  );
};
